import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import type { Account, AccountCategory, ApiResponse } from '../types';

export const accountCategoryLabels: Record<AccountCategory, string> = {
  asset: '資産',
  liability: '負債',
  equity: '純資産',
  revenue: '収益',
  expense: '費用',
};

export function useAccounts(includeInactive = false) {
  return useQuery({
    queryKey: ['accounts', { includeInactive }],
    queryFn: async () => {
      const path = includeInactive ? '/api/accounts?includeInactive=true' : '/api/accounts';
      const res = await api.get<ApiResponse<Account[]>>(path);
      return res.data;
    },
  });
}

type AccountInput = {
  code: string;
  name: string;
  category: AccountCategory;
  ntaCategoryCode?: string | null;
};

export function useCreateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AccountInput) =>
      api.post<ApiResponse<Account>>('/api/accounts', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounts'] }),
  });
}

export function useUpdateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: AccountInput & { id: string; isActive?: boolean }) =>
      api.patch<ApiResponse<Account>>(`/api/accounts/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounts'] }),
  });
}

export function useDeleteAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/accounts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounts'] }),
  });
}
